import { Injectable } from '@angular/core';

/**
 * Utility service for date operations
 * Centralizes date parsing, formatting, and calculations
 */
@Injectable()
export class DateUtilsService {

    // Time constants in milliseconds
    private readonly MS_SECOND = 1000;
    private readonly MS_MINUTE = this.MS_SECOND * 60;
    private readonly MS_HOUR = this.MS_MINUTE * 60;
    private readonly MS_DAY = this.MS_HOUR * 24;

    constructor() { }

    /**
     * Parses a date string in YYYY-MM-DD format to timestamp
     * Avoids timezone conversion issues
     * @param dateStr - Date string in format "YYYY-MM-DD"
     * @param endOfDay - If true, sets time to 23:59:59, otherwise 00:00:00
     * @returns Timestamp in milliseconds
     */
    parseDateStringToTimestamp(dateStr: string, endOfDay: boolean = false): number {
        const parts = dateStr.split('-');
        const year = parseInt(parts[0]);
        const month = parseInt(parts[1]) - 1; // month is 0-indexed
        const day = parseInt(parts[2]);

        if (endOfDay) {
            return new Date(year, month, day, 23, 59, 59).getTime();
        } else {
            return new Date(year, month, day, 0, 0, 0).getTime();
        }
    }

    /**
     * Formats a Date object to YYYY-MM-DD string
     * @param date - Date to format
     * @returns Date string in YYYY-MM-DD format
     */
    toISODateString(date: Date): string {
        return date.toISOString().substring(0, 10);
    }

    /**
     * Calculates time remaining until expiration (24 hours from given date)
     * @param fechaAceptacion - Acceptance date timestamp
     * @returns Formatted string (e.g., "1d 5h 30m") or "Caducada" if expired
     */
    getRemainingTime(fechaAceptacion: number): string {
        if (!fechaAceptacion) {
            return 'Caducada';
        }

        const end = new Date(fechaAceptacion);
        const now = new Date();
        const fechaExpiracion = end.getTime() + this.MS_DAY;

        const distance = fechaExpiracion - now.getTime();
        if (distance < 0) {
            return 'Caducada';
        }

        return this.formatTimeInterval(distance);
    }

    /**
     * Calculates time elapsed since a given date
     * @param fechaExpiracion - Date timestamp
     * @returns Formatted string (e.g., "2h 15m") or "Caducada" if negative
     */
    getTimeSince(fechaExpiracion: number): string {
        const end = new Date(fechaExpiracion);
        const now = new Date();

        const distance = now.getTime() - end.getTime();
        if (distance < 0) {
            return 'Caducada';
        }

        return this.formatTimeInterval(distance);
    }

    /**
     * Calculates hours between two dates
     * @param fecha1 - First date timestamp
     * @param fecha2 - Second date timestamp
     * @returns Hours between dates, or -1 if fecha2 is before fecha1
     */
    getHoursBetween(fecha1: number, fecha2: number): number {
        const end = new Date(fecha1);
        const now = new Date(fecha2);

        const distance = now.getTime() - end.getTime();
        if (distance < 0) {
            return -1;
        }
        return Math.floor((distance % this.MS_DAY) / this.MS_HOUR);
    }

    /**
     * Calculates hours since a given date
     * @param fechaExpiracion - Date timestamp
     * @returns Hours since date, or -1 if in future
     */
    getHoursSince(fechaExpiracion: number): number {
        const end = new Date(fechaExpiracion);
        const now = new Date();

        const distance = now.getTime() - end.getTime();
        if (distance < 0) {
            return -1;
        }
        return Math.floor((distance % this.MS_DAY) / this.MS_HOUR);
    }

    /**
     * Calculates elapsed business minutes between two timestamps, excluding off-hours.
     * Off-hours window is defined by offHoursStart–midnight–offHoursEnd (e.g. 19:00–09:00).
     * Walks the interval in business-hours blocks; never iterates minute-by-minute.
     * @param startMs - Start timestamp in ms
     * @param endMs - End timestamp in ms
     * @param offHoursStart - Hour (0-23) when off-hours begin (inclusive), e.g. 19
     * @param offHoursEnd - Hour (0-23) when business hours resume (inclusive), e.g. 9
     * @param isSkipDay - Optional: whole calendar days this returns true for (weekends, holidays)
     *   contribute zero business minutes. Omit to preserve the original every-day behaviour.
     * @returns Elapsed business minutes (0 if end <= start or dates are invalid)
     */
    getBusinessElapsedMinutes(startMs: number, endMs: number, offHoursStart: number, offHoursEnd: number, isSkipDay?: (d: Date) => boolean): number {
        if (!startMs || !endMs || endMs <= startMs) {
            return 0;
        }

        const offStartMin = offHoursStart * 60;
        const offEndMin = offHoursEnd * 60;
        let businessMinutes = 0;
        let cursor = startMs;

        while (cursor < endMs) {
            const d = new Date(cursor);

            if (isSkipDay && isSkipDay(d)) {
                // Whole day doesn't count — jump straight to offHoursEnd:00 tomorrow and re-check.
                const nextDay = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1, offHoursEnd, 0, 0, 0);
                cursor = nextDay.getTime();
                continue;
            }

            const h = d.getHours();
            const minuteOfDay = h * 60 + d.getMinutes();
            const isOffHours = h >= offHoursStart || h < offHoursEnd;

            if (!isOffHours) {
                // Advance to the next off-hours start (same day at offHoursStart:00)
                const minutesUntilOff = offStartMin - minuteOfDay;
                const blockEnd = cursor + minutesUntilOff * this.MS_MINUTE;
                const actualEnd = blockEnd < endMs ? blockEnd : endMs;
                businessMinutes += Math.floor((actualEnd - cursor) / this.MS_MINUTE);
                cursor = blockEnd;
            } else if (h < offHoursEnd) {
                // In 00:00–offHoursEnd window — jump to offHoursEnd:00 today
                const minutesUntilBusiness = offEndMin - minuteOfDay;
                cursor = cursor + minutesUntilBusiness * this.MS_MINUTE;
            } else {
                // In offHoursStart:00–24:00 window — jump to offHoursEnd:00 tomorrow
                const minutesUntilMidnight = 24 * 60 - minuteOfDay;
                const midnight = cursor + minutesUntilMidnight * this.MS_MINUTE;
                cursor = midnight + offEndMin * this.MS_MINUTE;
            }
        }

        return businessMinutes;
    }

    /**
     * Adjusts JavaScript weekday (0=Sunday) to Monday-first (0=Monday)
     * @param weekday - JavaScript weekday (0-6, where 0 is Sunday)
     * @returns Adjusted weekday (0-6, where 0 is Monday)
     */
    getMondayFirstWeekday(weekday: number): number {
        return (weekday + 6) % 7;
    }

    /**
     * Formats a minute count as "Xh Ym" / "Xh" / "Ym"
     * @param totalMinutes - Minute count (e.g. from getBusinessElapsedMinutes)
     * @returns Formatted string, or '-' when totalMinutes is 0/falsy
     */
    formatMinutes(totalMinutes: number): string {
        if (!totalMinutes || totalMinutes <= 0) {
            return '-';
        }
        const hours = Math.floor(totalMinutes / 60);
        const minutes = Math.round(totalMinutes % 60);
        if (hours > 0 && minutes > 0) {
            return hours + 'h ' + minutes + 'm';
        }
        if (hours > 0) {
            return hours + 'h';
        }
        return minutes + 'm';
    }

    /**
     * Formats a time interval in milliseconds to human-readable string
     * @param distanceMs - Time interval in milliseconds
     * @returns Formatted string (e.g., "1d 5h 30m" or "45s")
     */
    private formatTimeInterval(distanceMs: number): string {
        const days = Math.floor(distanceMs / this.MS_DAY);
        const hours = Math.floor((distanceMs % this.MS_DAY) / this.MS_HOUR);
        const minutes = Math.floor((distanceMs % this.MS_HOUR) / this.MS_MINUTE);
        const seconds = Math.floor((distanceMs % this.MS_MINUTE) / this.MS_SECOND);

        let result = '';
        if (days > 0) result += days + 'd ';
        if (hours > 0) result += hours + 'h ';
        if (minutes > 0) result += minutes + 'm ';
        if (result === '') {
            result = seconds + 's';
        }
        return result;
    }
}



// WEBPACK FOOTER //
// E:/Github/trosten/trosten-web-app/src/app/services/helpers/date-utils.service.ts